import { action, flow, makeObservable, observable } from 'mobx';
import BaseStore from './BaseStore';
import RootStore from '.';
import CartApi from '@shared/apis/CartApi';
import PaymentApi from '@shared/apis/PaymentApi';
import OrderApi from '@shared/apis/OrderApi';
import { CartItem, CartItemWithChecked, PreCheckoutCartResponse } from '@shared/types/cart';
import { ResponseData } from '@shared/types/http';
import { toastify } from '@shared/utils/toastify';
import { CheckoutOrderDetail } from '@shared/types/order';

export default class CartStore extends BaseStore {
    items: CartItemWithChecked[] = [];
    cartApi: CartApi;
    paymentApi: PaymentApi;
    orderApi: OrderApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        this.cartApi = new CartApi();
        this.paymentApi = new PaymentApi();
        this.orderApi = new OrderApi();
        makeObservable(this, {
            items: observable,
            toggleItem: action.bound,
            toggleAllItems: action.bound,
            getSubtotal: action.bound,
            getTax: action.bound,
            getTotal: action.bound,
            fetchCartItems: flow.bound,
            getPreCheckoutCart: flow.bound,
            getPaymentUrl: flow.bound,
            getOrderDetail: flow.bound,
            addToCart: flow.bound,
            deleteToCart: flow.bound,
            clearCart: action.bound
        });
    }

    /**
     * Add item to cart via API
     * @param publicationId Publication ID to add to cart
     */
    *addToCart(publicationId: number) {
        try {
            const res: ResponseData<string> = yield this.rootStore.apiStore.call(
                this.cartApi,
                () => this.cartApi.addToCart(publicationId)
            );

            if (res?.ok) {
                toastify('success', '商品をカートに追加しました');
                yield this.fetchCartItems();
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Failed to add item to cart:', error);
            toastify('error', '商品の追加に失敗しました');
            return false;
        }
    }

    /**
     * Delete item from cart via API
     * @param publicationId Publication ID to remove from cart
     */
    *deleteToCart(publicationId: number) {
        try {
            const res: ResponseData<string> = yield this.rootStore.apiStore.call(
                this.cartApi,
                () => this.cartApi.deleteToCart(publicationId)
            );

            if (res?.ok) {
                // Remove item from local state immediately for better UX
                this.items = this.items.filter(item => item.publicationId !== publicationId);
                toastify('success', '商品を削除しました');
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Failed to delete item from cart:', error);
            toastify('error', '商品の削除に失敗しました');
            return false;
        }
    }

    toggleItem(itemId: number) {
        const item = this.items.find(i => i.id === itemId);
        if (item) {
            item.checked = !item.checked;
        }
    }

    toggleAllItems(checked: boolean) {
        this.items.forEach((item) => {
            item.checked = checked;
        });
    }

    clearCart() {
        this.items = [];
    }

    getSubtotal(): number {
        return this.items
            .filter(item => item.checked)
            .reduce((sum, item) => sum + item.publication.price, 0);
    }

    getTax(taxRate: number = 0.1): number {
        return Math.round(this.getSubtotal() * taxRate);
    }

    getTotal(taxRate: number = 0.1): number {
        return this.getSubtotal() + this.getTax(taxRate);
    }

    /**
     * Fetch cart items from API and sync to store
     */
    *fetchCartItems() {
        try {
            const res: ResponseData<CartItem[]> = yield this.rootStore.apiStore.call(
                this.cartApi,
                this.cartApi.getAllCarts
            );

            if (res?.ok) {
                const cartData = res.data || [];

                // Map API response to CartItem format
                this.items = cartData.map((item: any) => ({
                    ...item,
                    checked: true
                }));

                return this.items;
            }
            return [];
        } catch (error: any) {
            console.error('Failed to fetch cart items:', error);
        }
    }

    /**
     * Get pre-checkout cart information for selected publications
     * @param publicationIds Array of publication IDs to checkout
     */
    *getPreCheckoutCart(publicationIds: string[]) {
        try {
            const res: ResponseData<PreCheckoutCartResponse> = yield this.rootStore.apiStore.call(
                this.cartApi,
                () => this.cartApi.getPreCheckoutCart(publicationIds)
            );

            return res.data;
        } catch (error: any) {
            console.error('Failed to get pre-checkout cart:', error);
        }
    }

    /**
     * Get GMO payment URL for checkout
     * @param publicationIds Array of publication IDs to checkout
     * @param priceTotal Total price before tax
     * @param taxTotal Tax amount
     * @param grandTotal Total price including tax
     */
    *getPaymentUrl(
        publicationIds: string[],
        priceTotal: number,
        taxTotal: number,
        grandTotal: number
    ) {
        try {
            const res: ResponseData<{ paymentUrl: string, orderId: number, orderCode: string, isOrderFree: boolean }> = yield this.rootStore.apiStore.call(
                this.paymentApi,
                () => this.paymentApi.getPaymentUrl({
                    publicationIds,
                    priceTotal,
                    taxTotal,
                    grandTotal
                })
            );

            if (res?.ok) {
                return res.data;
            }
        } catch (error: any) {
            throw error;
        }
    }

    /**
     * Get order detail by order ID to verify payment status
     * @param orderId Order ID
     */
    *getOrderDetail(orderId: string | number) {
        try {
            const res: ResponseData<CheckoutOrderDetail> = yield this.rootStore.apiStore.call(
                this.orderApi,
                () => this.orderApi.getOrderDetail(orderId)
            );

            if (res?.ok) {
                return res.data;
            }
        } catch (error: any) {
            console.error('Failed to get order detail:', error);
        }
    }

    clean() {
        super.clean();
        this.items = [];
    }
}
